import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';

import {
  GetCertifiedPlantIdByDeviceIdQuery,
  GetCertifiedPlantIdByEmsIdQuery,
  GetConnextDataQuery,
  GetConnextIdBySnQuery,
  GetConnextSnByIdQuery,
  GetLoggerDataQuery,
} from '../telemetry/index.graph-ql';
import { connextOwnerQuery, getCertifiedPlantIdFragment, uidQuery, azzurroHubDataQuery } from './query.gql';
import { TelemetryService } from '../telemetry-service/telemetry.service';
import { ProductModelService } from '../models';
import { checkFwVersion, getMeterSerial, isAdmin, isConnected, isOwner } from '../utils';
import { DeviceType, FotaStatus } from '../models/firebase.model';
import { deviceSerialValidator } from './device-serial.validator';
import { AzzurroHubDataType, ConnextData } from '../telemetry';

interface GenericDeviceValidationOptions {
  connextFwMinVersion?: string;
  azzurroHubFwMinVersion?: string;
  certifiedPlant?: boolean;
}

/**
 * Extract value from an object given a nested key, e.g., 'attribute.inner_attribute',
 * using optional chaining to handle undefined values.
 */
function getValueByPath(obj: any, path: string): any {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
}

@Injectable({ providedIn: 'root' })
export class GenericDeviceValidator {
  constructor(
    private readonly getCertifiedPlantIdByDeviceIdQuery: GetCertifiedPlantIdByDeviceIdQuery,
    private readonly getCertifiedPlantIdByEmsIdQuery: GetCertifiedPlantIdByEmsIdQuery,
    private readonly getConnextDataQuery: GetConnextDataQuery,
    private readonly getConnextIdBySnQuery: GetConnextIdBySnQuery,
    private readonly getConnextSnByIdQuery: GetConnextSnByIdQuery,
    private readonly getLoggerDataQuery: GetLoggerDataQuery,
    private readonly telemetryService: TelemetryService,
  ) {
    this.deviceExist = this.deviceExist.bind(this);
    this.connextCheck = this.connextCheck.bind(this);
    this.azzurroHubCheck = this.azzurroHubCheck.bind(this);
  }

  deviceTypeExists(type?: DeviceType[], opt: GenericDeviceValidationOptions = {}) {
    const checkType = deviceSerialValidator(type);
    return async (control: AbstractControl): Promise<ValidationErrors | null> => {
      if (checkType(control) !== null) {
        return checkType(control);
      }

      return await this.deviceExist(opt)(control);
    };
  }


  /**
   * @abstract Validator for unique device id, useKey supports nested keys e.g., 'attribute.inner_attribute'
   */
  deviceUnique(
    itemList?: Array<{ [key: string]: any }>,
    transformValue?: (value: string) => Promise<string>,
    useKey: string = 'id',
  ) {
    return async (control: AbstractControl): Promise<ValidationErrors | null> => {
      let value = control.value.trim().toLowerCase();
      if (!value) {
        return null;
      }

      if (transformValue) {
        value = await transformValue(value);
      }

      const itemListWithKey = itemList?.filter((device) => {
        const keyValue = getValueByPath(device, useKey)?.toLowerCase();
        return keyValue === value;
      });

      return itemListWithKey?.length ? { unique: true, itemList: itemListWithKey, value } : null;
    };
  }

  deviceExist(opt: GenericDeviceValidationOptions = {}) {
    return async (control: AbstractControl): Promise<ValidationErrors | null> => {
      let deviceId: string = control.value.trim();
      if (!deviceId) {
        return null;
      }

      const product = new ProductModelService(deviceId);
      if (product.type?.name === 'connext') {
        return await this.connextCheck(opt)(deviceId);
      }

      if (product.type?.name === 'azzurro-hub') {
        return await this.azzurroHubCheck(opt)(deviceId);
      }

      if (product.type?.name === 'meter') {
        deviceId = getMeterSerial(deviceId);
      }

      try {
        const updatedAt = await this.telemetryService.checkIfExist(deviceId);
        return updatedAt ? null : { deviceNotExist: true };
      } catch {
        return { genericError: true };
      }
    }
  }


  connextCheck(opt: GenericDeviceValidationOptions = {}) {
    const networkOnly = true;
    return async (deviceId: string): Promise<ValidationErrors | null> => {
      let uid: string | null | undefined = '';
      try {
        uid = await this.getConnextUid(deviceId);
        if (!uid) {
          return { deviceNotExist: true };
        }

        if (opt.certifiedPlant) {
          const control = { value: uid } as AbstractControl;
          const checkIfAlreadyInPlant = await this.checkAvailableEMSWithoutCertifiedPlant()(control);
          if (checkIfAlreadyInPlant) {
            return checkIfAlreadyInPlant;
          }
        }

        const { data } = await this.getConnextDataQuery.execute(connextOwnerQuery, { id: uid }, networkOnly);
        return this.checkConnextData(uid, data.getConnextData, opt);

      } catch {
        return { genericError: true };
      }
    }
  }

  azzurroHubCheck(opt: GenericDeviceValidationOptions = {}) {
    const networkOnly = true;
    return async (deviceSerial: string): Promise<ValidationErrors | null> => {
      try {
        // Validate Azzurro Hub serial pattern (AH + 10 digits)
        if (!/^AH\d{10}$/.test(deviceSerial.toUpperCase())) {
          return { serialNotValid: true };
        }

        const serial = deviceSerial.toUpperCase();

        // For Azzurro Hub, we check if it's already in a certified plant
        if (opt.certifiedPlant) {
          const control = { value: serial } as AbstractControl;
          const checkIfAlreadyInPlant = await this.checkAvailableEMSWithoutCertifiedPlant()(control);
          if (checkIfAlreadyInPlant) {
            return checkIfAlreadyInPlant;
          }
        }

        // Get Azzurro Hub data using getLoggerData query (with caching)
        const azzurroHubData = await this.getAzzurroHubData(serial, networkOnly);
        if (!azzurroHubData) {
          return { deviceNotExist: true };
        }

        // Check Azzurro Hub data for validation errors
        return this.checkAzzurroHubData(serial, azzurroHubData, opt);

      } catch {
        return { genericError: true };
      }
    }
  }

  private async getAzzurroHubData(serial: string, networkOnly: boolean): Promise<AzzurroHubDataType | null> {
    try {
      const results = await this.getLoggerDataQuery.execute(azzurroHubDataQuery, { id: serial }, networkOnly);
      return results.data.getLoggerData as AzzurroHubDataType;
    } catch {
      return null;
    }
  }

  private checkAzzurroHubData(serial: string, loggerData: any, opt: GenericDeviceValidationOptions = {}): ValidationErrors | null {
    // Check if it's actually Azzurro Hub data
    if (!loggerData?.loggerId || !loggerData?.info || !loggerData?.config) {
      return { deviceNotExist: true };
    }

    // Check firmware version
    let fwVersionNotValid = false;
    if (opt.azzurroHubFwMinVersion && loggerData.info?.fw?.currVer) {
      if (!checkFwVersion(loggerData.info.fw.currVer, opt.azzurroHubFwMinVersion)) {
        fwVersionNotValid = true;
      }
    }

    // Check if device is online using connection status
    let deviceNotOnline = false;
    if (loggerData.info?.connection?.status !== 'connected') {
      deviceNotOnline = true;
    }

    // Check if Azzurro Hub is busy
    if (loggerData.config?.operational === 'busy') {
      return { azzurroHubBusy: true, serial };
    }

    // Check if device scan is in progress
    if (loggerData.config?.scanDevices === 'scanning') {
      return { azzurroHubScanInProgress: true, serial };
    }

    /**
     * IMPORTANT: Azzurro Hub doesn't have ownership concept like Connext
     * It can only have owners if part of a certified plant
     * We don't return hasOwnerAndItsNotMe or iamAlreadyAdmin errors here
     */

    // Check if device is offline
    if (deviceNotOnline) {
      return { deviceNotOnline: true, fwVersionNotValid, serial };
    }

    // Check firmware version
    if (fwVersionNotValid) {
      return { fwVersionNotValid: true, serial };
    }

    return null;
  }

  checkConnextData(uid: string, connextData: Partial<ConnextData>, opt: GenericDeviceValidationOptions = {}) {
    /**
     * Check if device has minimum firmware version and is online
     */
    let fwVersionNotValid = false;
    if (!checkFwVersion(connextData?.info?.fw?.fw_ver, opt.connextFwMinVersion)) {
      fwVersionNotValid = true;
    }
    let deviceNotOnline = false;
    if (!isConnected(connextData)) {
      deviceNotOnline = true;
    }

    if (connextData.info?.fw.fota_status === FotaStatus.Updating) {
      return { fwUpdating: true, uid };
    }

    /**
     * check if has owner and if iam owner or admin
     */
    if (connextData.hasOwner && !(isOwner(connextData) || isAdmin(connextData))) {
      return { hasOwnerAndItsNotMe: true, fwVersionNotValid, uid, deviceNotOnline };
    }

    if (!opt.certifiedPlant) {
      /**
       * Check if has owner and iam already owner or admin
       */
      if (connextData.hasOwner && (isOwner(connextData) || isAdmin(connextData))) {
        return { iamAlreadyAdmin: true, fwVersionNotValid, uid, deviceNotOnline };
      }
    }

    /**
     * Check if device is online
     */
    if (deviceNotOnline) {
      return { deviceNotOnline: true, fwVersionNotValid, uid };
    }

    /**
     * Check if device has minimum firmware version
     */
    if (fwVersionNotValid) {
      return { fwVersionNotValid: true, uid };
    }

    return null;
  }

  async getConnextUid(deviceId: string) {
    /**
     * This query should not change over time, so we can use networkOnly = false
     */
    const networkOnly = false;
    try {
      /**
       * Sn is 14 characters long
       */
      if (deviceId.length === 14) {
        const { data } = await this.getConnextIdBySnQuery.execute(uidQuery, { sn: deviceId.toUpperCase() }, networkOnly);
        return data.getConnextIdBySn.uid;
      } else if (deviceId.length === 24) {
        /**
         * Uid is 24 characters long
         */
        const { data } = await this.getConnextSnByIdQuery.execute(uidQuery, { id: deviceId.toLowerCase() }, networkOnly);
        return data.getConnextSnById.uid;
      }
    } catch { }
    return null;
  }

  checkAvailableEMSWithoutCertifiedPlant() {
    return async (control: AbstractControl): Promise<ValidationErrors | null> => {
      const emsId = control.value.trim();
      if (!emsId) {
        return null;
      }

      try {
        const networkOnly = true;
        const { data } = await this.getCertifiedPlantIdByEmsIdQuery.execute(getCertifiedPlantIdFragment, { emsId }, networkOnly);
        const plantId = data.getCertifiedPlantIdByEmsId.plantId;
        if (plantId) {
          return { deviceAlreadyOnACertifiedPlant: true, plantId };
        }
        return null;
      } catch {
        return { genericError: true };
      }
    }
  }

  checkAvailableDeviceWithCertifiedPlant(currentPlantId = '') {
    return async (control: AbstractControl): Promise<ValidationErrors | null> => {
      const deviceId = control.value.trim();
      if (!deviceId) {
        return null;
      }

      try {
        const networkOnly = true;
        const { data } = await this.getCertifiedPlantIdByDeviceIdQuery.execute(getCertifiedPlantIdFragment, { deviceId }, networkOnly);
        const plantId = data.getCertifiedPlantIdByDeviceId.plantId;
        if (plantId && plantId !== currentPlantId) {
          return { deviceAlreadyOnACertifiedPlant: true, plantId };
        }
        return null;
      } catch {
        return { genericError: true };
      }
    }
  }
}
